/**
 * @fileoverview Document Management Helpers for Payment Processing
 *
 * This module handles document-related business logic for payments, including:
 * - Document change detection for unsaved changes warnings
 * - Document cleanup workflows when payments are cancelled
 * - Document status tracking (saved vs pending)
 * - Document title standardization for consistency
 * - Invoice document extraction and management
 *
 * Business Context:
 * - Payments can have receipt documents and invoice documents
 * - Documents are uploaded before payment creation and need cleanup if cancelled
 * - Existing payments can have receipt changes that need tracking
 * - Document validation and title formatting follows organizational standards
 * - Pending documents must be cleaned up to prevent orphaned files
 *
 * @author Extracted during PaymentDialog refactoring
 * @since Phase 1 refactoring - business logic extraction
 */

import { DocumentType } from "generated-graphql/graphql";

/**
 * Detect unsaved document changes for confirmation dialogs
 *
 * Determines if there are pending document changes that would be lost
 * if the user closes the payment dialog without saving. Used to show
 * appropriate confirmation dialogs.
 *
 * Business Rules:
 * - New payments: Any uploaded documents are considered unsaved
 * - Existing payments: Only receipt changes are tracked (invoices can't be changed)
 *
 * @param paymentId - Payment ID (undefined for new payments)
 * @param pendingInvoices - Array of uploaded invoice documents
 * @param currentReceipt - Currently selected receipt document
 * @param originalReceipt - Original receipt from existing payment
 * @returns True if there are unsaved document changes
 */
export function checkForUnsavedDocumentChanges(
  paymentId: string | undefined,
  pendingInvoices: any[],
  currentReceipt: any | undefined,
  originalReceipt: any | undefined
): boolean {
  if (!paymentId) {
    // New payment: has changes if any documents uploaded
    return pendingInvoices.length > 0 || !!currentReceipt;
  } else {
    // Existing payment: check if receipt has changed
    // Only check for changes if form is initialized (currentReceipt !== undefined)
    if (currentReceipt === undefined) {
      return false;
    }

    const currentId = currentReceipt?.id;
    const originalId = originalReceipt?.id;
    const hasChanged = currentId !== originalId;

    return hasChanged;
  }
}

/**
 * Clean up uploaded documents when payment creation is cancelled
 *
 * Deletes documents from the database when a user cancels payment creation
 * or closes the dialog without saving. Prevents orphaned documents that
 * aren't associated with any payment.
 *
 * Business Logic:
 * - New payments: Delete all uploaded documents (invoices + receipts)
 * - Existing payments: Only delete changed receipt documents
 * - Graceful error handling - doesn't block dialog closing on cleanup failure
 *
 * @param paymentId - Payment ID (undefined for new payments)
 * @param pendingInvoices - Array of invoice documents to clean up
 * @param currentReceipt - Current receipt document
 * @param originalReceipt - Original receipt document (for existing payments)
 * @param deleteDocument - GraphQL mutation function for document deletion
 * @returns Promise that resolves when cleanup is complete
 */
export async function handleDocumentCleanup(
  paymentId: string | undefined,
  pendingInvoices: any[],
  currentReceipt: any | undefined,
  originalReceipt: any | undefined,
  deleteDocument: (variables: { variables: { id: string } }) => Promise<any>
): Promise<void> {
  try {
    if (!paymentId) {
      // New payments: delete all uploaded documents
      const allDocumentIds = [
        ...pendingInvoices.map((f) => f.invoiceDocumentId),
        currentReceipt?.id,
      ].filter(Boolean) as string[];

      for (const docId of allDocumentIds) {
        await deleteDocument({ variables: { id: docId } });
      }
    } else {
      // Existing payments: only delete pending receipt if different from original
      if (currentReceipt?.id && currentReceipt.id !== originalReceipt?.id) {
        await deleteDocument({ variables: { id: currentReceipt.id } });
      }
    }
  } catch (error) {
    // Log error but don't block dialog close
    console.error("Error cleaning up uploaded documents:", error);
  }
}

/**
 * Extract invoice document ID from payment invoices
 *
 * Finds the first invoice document ID from a payment's invoice collection.
 * Used for Lob payments which require exactly one invoice document.
 *
 * @param paymentInvoices - Array of payment invoice objects
 * @returns Invoice document ID or empty string if not found
 */
export function getInvoiceDocumentId(paymentInvoices: any[] | undefined): string {
  return (
    paymentInvoices?.find((pi) => pi.invoiceDocument?.documentType === DocumentType.Invoice)
      ?.invoiceDocument?.id ?? ""
  );
}

/**
 * Add status indicator to document objects
 *
 * Enhances document objects with status information for UI display,
 * indicating whether documents are saved to database or pending upload.
 *
 * @param document - Document object to enhance
 * @param status - Status to add (e.g., 'saved', 'pending')
 * @returns Enhanced document object with status or null if input is null
 */
export function formatDocumentWithStatus(
  document: any | null,
  status: "saved" | "pending"
): any | null {
  if (!document) return null;
  return { ...document, status };
}
